import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import {CheckIcon, ChevronDownIcon, ChevronUpIcon} from 'lucide-react';
import {cn} from '../../lib/cn';

const LegacySelectRoot = SelectPrimitive.Root;
const LegacySelectGroup = SelectPrimitive.Group;
const LegacySelectValue = SelectPrimitive.Value;
const LegacySelectPortal = SelectPrimitive.Portal;

type LegacySelectSize = 'sm' | 'md';

const selectSizeClassNames: Record<LegacySelectSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
};

const LegacySelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    size?: LegacySelectSize;
  }
>(({className, children, size = 'sm', ...props}, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-between gap-2 rounded-lg border border-[var(--chekin-gray-200)] bg-white px-3 py-2 text-start text-[var(--chekin-color-brand-navy)] shadow-[0_1px_2px_rgb(0_0_0_/_0.05)] shadow-black/5 outline-none',
      'focus:border-[var(--chekin-color-brand-blue)] focus:shadow-[var(--chekin-shadow-focus)]',
      'data-[placeholder]:text-[var(--chekin-color-gray-1)]',
      'disabled:cursor-not-allowed disabled:opacity-50 [&>span]:min-w-0',
      selectSizeClassNames[size],
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon
        size={16}
        strokeWidth={2}
        className="shrink-0 text-[var(--chekin-color-gray-1)]"
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
LegacySelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const LegacySelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({className, ...props}, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUpIcon size={16} strokeWidth={2} />
  </SelectPrimitive.ScrollUpButton>
));
LegacySelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const LegacySelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({className, ...props}, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDownIcon size={16} strokeWidth={2} />
  </SelectPrimitive.ScrollDownButton>
));
LegacySelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const LegacySelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({className, children, position = 'popper', ...props}, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden rounded-lg border border-[var(--chekin-gray-200)] bg-white text-[var(--chekin-color-brand-navy)] shadow-lg shadow-black/5',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        '[&_[role=group]]:py-1',
        position === 'popper' &&
          'w-full min-w-[var(--radix-select-trigger-width)] data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <LegacySelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' && 'h-[var(--radix-select-trigger-height)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <LegacySelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
LegacySelectContent.displayName = SelectPrimitive.Content.displayName;

const LegacySelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({className, ...props}, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'py-1.5 pe-2 ps-8 text-xs font-medium text-[var(--chekin-color-gray-1)]',
      className,
    )}
    {...props}
  />
));
LegacySelectLabel.displayName = SelectPrimitive.Label.displayName;

const LegacySelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    size?: LegacySelectSize;
  }
>(({className, children, size = 'sm', ...props}, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pe-2 ps-8 outline-none',
      'focus:bg-[var(--chekin-gray-50)] focus:text-[var(--chekin-color-brand-navy)]',
      'data-[highlighted]:bg-[var(--chekin-gray-50)] data-[highlighted]:text-[var(--chekin-color-brand-navy)]',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      selectSizeClassNames[size],
      className,
    )}
    {...props}
  >
    <span className="absolute start-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon size={16} strokeWidth={2} />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
LegacySelectItem.displayName = SelectPrimitive.Item.displayName;

const LegacySelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({className, ...props}, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-[var(--chekin-gray-100)]', className)}
    {...props}
  />
));
LegacySelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  LegacySelectRoot,
  LegacySelectContent,
  LegacySelectGroup,
  LegacySelectItem,
  LegacySelectLabel,
  LegacySelectPortal,
  LegacySelectScrollDownButton,
  LegacySelectScrollUpButton,
  LegacySelectSeparator,
  LegacySelectTrigger,
  LegacySelectValue,
};
export type {LegacySelectSize};
